import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString } from "geojson";
import type mapboxgl from "mapbox-gl";

import { ensurePreviewCircleLayer, ensurePreviewLayer, removePreviewLayer, setPreviewFeatures } from "./snapPreview";
import { buildPathCoordinates, dijkstra, EdgeId, NodeId, ShortestPathResult, UgPathGraph } from "./ugPathGraph";

const { constants: Constants } = MapboxDraw;

const PATH_SOURCE_ID = "shortest-path-preview-source";
const PATH_LAYER_ID = "shortest-path-preview-layer";
const TENTATIVE_SOURCE_ID = "shortest-path-tentative-source";
const TENTATIVE_LAYER_ID = "shortest-path-tentative-layer";
const NODE_SOURCE_ID = "shortest-path-node-source";
const NODE_LAYER_ID = "shortest-path-node-layer";

const SNAP_THRESHOLD_PX = 15;
// Two clicks within this threshold on the same node are treated as a double-click that commits
// the accumulated path.
const DOUBLE_CLICK_MS = 300;

type Phase = "awaiting-start" | "building-path";

interface ShortestPathState {
  phase: Phase;
  startNodeId: NodeId | null;
  currentTailNodeId: NodeId | null;
  committedEdgeIds: EdgeId[];
  shortestPaths: Map<NodeId, ShortestPathResult> | null;
  lastHoveredNodeId: NodeId | null;
  lastClickAt: number;
  lastClickNodeId: NodeId | null;
}

function getGraph(this: MapboxDraw.DrawCustomModeThis): UgPathGraph | null {
  return (this._ctx.options.shortestPathGraph as UgPathGraph | undefined) ?? null;
}

/**
 * Finds the graph node nearest to the given screen point, within `thresholdPx`. Projects every
 * node's coordinate through the current map transform — for typical ug_path networks this is
 * fast enough without a spatial index.
 */
function findNearestNodeByPixel(graph: UgPathGraph, map: mapboxgl.Map, mouseX: number, mouseY: number, thresholdPx: number): NodeId | null {
  let best: NodeId | null = null;
  let bestSq = thresholdPx * thresholdPx;
  graph.nodes.forEach(([lng, lat], nodeId) => {
    const p = map.project([lng, lat]);
    const dx = p.x - mouseX;
    const dy = p.y - mouseY;
    const sq = dx * dx + dy * dy;
    if (sq < bestSq) {
      best = nodeId;
      bestSq = sq;
    }
  });
  return best;
}

function renderHoveredNode(map: mapboxgl.Map, graph: UgPathGraph, nodeId: NodeId | null): void {
  if (!nodeId) {
    setPreviewFeatures(map, NODE_SOURCE_ID, []);
    return;
  }
  const coord = graph.nodes.get(nodeId);
  if (!coord) return;
  setPreviewFeatures(map, NODE_SOURCE_ID, [
    {
      type: "Feature",
      properties: {},
      geometry: { type: "Point", coordinates: [coord[0], coord[1]] },
    },
  ]);
}

function lineFeature(coords: number[][]): Feature<LineString> {
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "LineString", coordinates: coords },
  };
}

function renderConfirmedPath(map: mapboxgl.Map, graph: UgPathGraph, startNodeId: NodeId, edgeIds: EdgeId[]): void {
  if (edgeIds.length === 0) {
    setPreviewFeatures(map, PATH_SOURCE_ID, []);
    return;
  }
  const coords = buildPathCoordinates(graph, startNodeId, edgeIds);
  if (coords.length < 2) {
    setPreviewFeatures(map, PATH_SOURCE_ID, []);
    return;
  }
  setPreviewFeatures(map, PATH_SOURCE_ID, [lineFeature(coords)]);
}

function renderTentativeLeg(map: mapboxgl.Map, graph: UgPathGraph, tailNodeId: NodeId, edgeIds: EdgeId[]): void {
  if (edgeIds.length === 0) {
    setPreviewFeatures(map, TENTATIVE_SOURCE_ID, []);
    return;
  }
  const coords = buildPathCoordinates(graph, tailNodeId, edgeIds);
  if (coords.length < 2) {
    setPreviewFeatures(map, TENTATIVE_SOURCE_ID, []);
    return;
  }
  setPreviewFeatures(map, TENTATIVE_SOURCE_ID, [lineFeature(coords)]);
}

function commitAccumulatedPath(this: MapboxDraw.DrawCustomModeThis, state: ShortestPathState): Feature[] {
  const graph = getGraph.call(this);
  const editingLayerId = this._ctx.options.editOptions?.editingLayerId;
  if (!graph || !editingLayerId || state.committedEdgeIds.length === 0) return [];

  const created: Feature[] = [];
  for (const edgeId of state.committedEdgeIds) {
    const edge = graph.edges.get(edgeId);
    if (!edge) continue;
    const newFeature = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: { layerId: editingLayerId, sourceUgPathId: edge.featureId },
      geometry: { type: "LineString", coordinates: edge.coordinates },
    });
    this.addFeature(newFeature);
    created.push(newFeature.toGeoJSON() as Feature);
  }
  return created;
}

function clearPreviewLayers(map: mapboxgl.Map): void {
  setPreviewFeatures(map, PATH_SOURCE_ID, []);
  setPreviewFeatures(map, TENTATIVE_SOURCE_ID, []);
  setPreviewFeatures(map, NODE_SOURCE_ID, []);
}

function resetState(state: ShortestPathState): void {
  state.phase = "awaiting-start";
  state.startNodeId = null;
  state.currentTailNodeId = null;
  state.committedEdgeIds = [];
  state.shortestPaths = null;
  state.lastHoveredNodeId = null;
  state.lastClickAt = 0;
  state.lastClickNodeId = null;
}

/**
 * Custom draw mode that builds footprint along a chain of shortest paths through the ug_path
 * network. First click sets the start; each subsequent click extends the path by the shortest
 * route from the last-clicked node. Double-click commits the accumulated path as one draw
 * feature per edge traversed.
 *
 * Configuration via MapboxDrawOptions:
 * - shortestPathGraph: precomputed UgPathGraph (required)
 * - editOptions.editingLayerId: which draw layer the committed footprint features land on
 *
 * Each committed footprint feature carries { layerId, sourceUgPathId } — same as snap_add.
 */
const shortestPathFeature: DrawCustomMode<ShortestPathState> = {
  onSetup(): ShortestPathState {
    this.updateUIClasses({ mouse: "add" });
    this.map.doubleClickZoom.disable();
    ensurePreviewLayer(this.map, PATH_SOURCE_ID, PATH_LAYER_ID, {
      "line-color": "#1976d2",
      "line-width": 6,
      "line-opacity": 0.7,
    });
    ensurePreviewLayer(this.map, TENTATIVE_SOURCE_ID, TENTATIVE_LAYER_ID, {
      "line-color": "#1976d2",
      "line-width": 4,
      "line-opacity": 0.5,
      "line-dasharray": [2, 2],
    });
    ensurePreviewCircleLayer(this.map, NODE_SOURCE_ID, NODE_LAYER_ID, {
      "circle-radius": 6,
      "circle-color": "#1976d2",
      "circle-stroke-color": "#ffffff",
      "circle-stroke-width": 2,
    });
    return {
      phase: "awaiting-start",
      startNodeId: null,
      currentTailNodeId: null,
      committedEdgeIds: [],
      shortestPaths: null,
      lastHoveredNodeId: null,
      lastClickAt: 0,
      lastClickNodeId: null,
    };
  },

  onMouseMove(state: ShortestPathState, event: MapboxDraw.MapMouseEvent) {
    const graph = getGraph.call(this);
    if (!graph) return;
    const hovered = findNearestNodeByPixel(graph, this.map, event.point.x, event.point.y, SNAP_THRESHOLD_PX);
    if (hovered === state.lastHoveredNodeId) return;
    state.lastHoveredNodeId = hovered;
    renderHoveredNode(this.map, graph, hovered);

    if (state.phase === "building-path" && state.currentTailNodeId && state.shortestPaths) {
      if (!hovered || hovered === state.currentTailNodeId) {
        setPreviewFeatures(this.map, TENTATIVE_SOURCE_ID, []);
        return;
      }
      const leg = state.shortestPaths.get(hovered);
      if (!leg) {
        setPreviewFeatures(this.map, TENTATIVE_SOURCE_ID, []);
        return;
      }
      renderTentativeLeg(this.map, graph, state.currentTailNodeId, leg.edgeIds);
    }
  },

  onClick(state: ShortestPathState, event: MapboxDraw.MapMouseEvent) {
    const graph = getGraph.call(this);
    if (!graph) return;
    const hovered = findNearestNodeByPixel(graph, this.map, event.point.x, event.point.y, SNAP_THRESHOLD_PX);
    if (!hovered) return;

    const now = Date.now();
    const isDoubleClick = state.lastClickNodeId === hovered && now - state.lastClickAt < DOUBLE_CLICK_MS;
    state.lastClickAt = now;
    state.lastClickNodeId = hovered;

    if (state.phase === "awaiting-start") {
      state.phase = "building-path";
      state.startNodeId = hovered;
      state.currentTailNodeId = hovered;
      state.committedEdgeIds = [];
      state.shortestPaths = dijkstra(graph, hovered);
      renderHoveredNode(this.map, graph, hovered);
      return;
    }

    // building-path
    if (isDoubleClick && state.committedEdgeIds.length > 0) {
      const created = commitAccumulatedPath.call(this, state);
      resetState(state);
      clearPreviewLayers(this.map);
      if (created.length > 0) {
        this.map.fire(Constants.events.CREATE, { features: created });
      }
      return;
    }

    // Extend path by the shortest-path leg from the current tail to the newly clicked node.
    if (!state.currentTailNodeId || !state.shortestPaths || !state.startNodeId) return;
    if (hovered === state.currentTailNodeId) return; // clicking same node = no-op
    const leg = state.shortestPaths.get(hovered);
    if (!leg || leg.edgeIds.length === 0) return; // unreachable
    state.committedEdgeIds = [...state.committedEdgeIds, ...leg.edgeIds];
    state.currentTailNodeId = hovered;
    state.shortestPaths = dijkstra(graph, hovered);
    renderConfirmedPath(this.map, graph, state.startNodeId, state.committedEdgeIds);
    setPreviewFeatures(this.map, TENTATIVE_SOURCE_ID, []);
    renderHoveredNode(this.map, graph, hovered);
  },

  onKeyUp(state: ShortestPathState, event: KeyboardEvent) {
    if (event.key !== "Escape") return;
    if (state.phase !== "building-path") return;
    resetState(state);
    clearPreviewLayers(this.map);
  },

  onStop() {
    this.map.doubleClickZoom.enable();
    removePreviewLayer(this.map, PATH_SOURCE_ID, PATH_LAYER_ID);
    removePreviewLayer(this.map, TENTATIVE_SOURCE_ID, TENTATIVE_LAYER_ID);
    removePreviewLayer(this.map, NODE_SOURCE_ID, NODE_LAYER_ID);
  },

  toDisplayFeatures(_state, geojson, display) {
    display(geojson);
  },
};

export default shortestPathFeature;
